import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  HiEye, 
  HiHeart, 
  HiScale, 
  HiUsers, 
  HiTrendingUp 
} from 'react-icons/hi';

const MethodologySectionV2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const pillars = [
    {
      icon: HiEye,
      title: 'Self-awareness',
      description: 'Understanding your patterns, triggers, and leadership style to lead with authenticity.',
      gradient: 'from-[#5B8FB9] to-[#7CB98F]',
      bgColor: 'bg-[#5B8FB9]/10',
    },
    {
      icon: HiHeart,
      title: 'Emotional Intelligence',
      description: 'Developing the ability to recognize, understand, and manage emotions effectively.',
      gradient: 'from-[#D4A5B5] to-[#5B8FB9]',
      bgColor: 'bg-[#D4A5B5]/10',
    },
    {
      icon: HiScale,
      title: 'Conscious Decision-making',
      description: 'Making choices aligned with values and long-term vision rather than reactive patterns.',
      gradient: 'from-[#7CB98F] to-[#5B8FB9]',
      bgColor: 'bg-[#7CB98F]/10',
    },
    {
      icon: HiUsers,
      title: 'Team Alignment',
      description: 'Creating shared purpose and clarity to drive collective performance.',
      gradient: 'from-[#5B8FB9] to-[#D4A5B5]',
      bgColor: 'bg-[#5B8FB9]/10',
    },
    {
      icon: HiTrendingUp,
      title: 'Sustainable Performance',
      description: 'Building systems and habits that maintain high performance without burnout.',
      gradient: 'from-[#7CB98F] to-[#D4A5B5]',
      bgColor: 'bg-[#7CB98F]/10',
    },
  ];

  return (
    <section
      id="methodology"
      ref={ref}
      data-testid="methodology-section-v2"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(91,143,185,0.05),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(212,165,181,0.05),transparent_50%)]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#7CB98F]/10 to-[#5B8FB9]/10 text-[#7CB98F] text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-[#7CB98F]/20">
            Methodology
          </span>
          <h2 
            className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="methodology-headline-v2"
          >
            My Approach
          </h2>
          <p className="text-lg text-gray-600 font-light max-w-2xl mx-auto" data-testid="methodology-description-v2">
            My work is built on a structured approach that integrates leadership development 
            with inner and organizational growth.
          </p>
        </motion.div>

        {/* Pillars - Colorful oval layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {pillars.map((pillar, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="group cursor-pointer"
              data-testid={`methodology-pillar-v2-${index + 1}`}
            >
              <div className={`${pillar.bgColor} group-hover:bg-gradient-to-br group-hover:${pillar.gradient} p-6 rounded-[2.5rem] text-center transition-all duration-500 h-full flex flex-col items-center border border-transparent group-hover:border-white/50 group-hover:shadow-xl`}>
                {/* Icon */}
                <motion.div 
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  className={`w-14 h-14 rounded-full bg-gradient-to-r ${pillar.gradient} flex items-center justify-center mb-4 shadow-lg`}
                >
                  <pillar.icon className="w-7 h-7 text-white" />
                </motion.div>
                
                {/* Title */}
                <h3 
                  className="text-lg font-medium text-gray-900 group-hover:text-white mb-3 transition-colors duration-300"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {pillar.title}
                </h3>
                
                {/* Description */}
                <p className="text-sm text-gray-600 group-hover:text-white/90 font-light leading-relaxed transition-colors duration-300">
                  {pillar.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Team image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 relative"
        >
          <div className="relative h-64 md:h-80 rounded-[3rem] overflow-hidden shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzQ5NzA3ODN8MA&ixlib=rb-4.1.0&q=85"
              alt="Team collaboration"
              className="w-full h-full object-cover"
              data-testid="methodology-image-v2"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#5B8FB9]/70 via-[#7CB98F]/50 to-transparent" />
            <div className="absolute bottom-8 left-8 md:left-12">
              <p className="text-white text-2xl md:text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Building teams that <span className="italic">thrive</span>
              </p>
            </div>
            
            {/* Decorative circles on image */}
            <div className="absolute top-4 right-4 w-16 h-16 border-2 border-white/30 rounded-full" />
            <div className="absolute top-8 right-8 w-8 h-8 bg-[#D4A5B5]/50 rounded-full" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MethodologySectionV2;
